import { useCallback } from 'react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { lookupLocal, fillTemplate } from '../../lib/brandText';

// ─────────────────────────────────────────────────────────────────────────────
// Strings for the FOREIGN onboarding form (PM-0061) — the `auth.onboarding.
// foreign.*` tree.
//
// They belong in the kit's auth.json and move there with the next kit release
// (the kit-lag rule, exactly as src/screens/MyPage/myPageStrings.js): until
// then they live here, and `useOnboardingStrings` asks the kit FIRST, so the
// day a released catalogue has a key the kit's wording wins and this entry is
// dead. Persian is authoritative; en and ar carry every key (parity is tested).
// ─────────────────────────────────────────────────────────────────────────────

import fa from './strings/fa.json';
import en from './strings/en.json';
import ar from './strings/ar.json';

const flatten = (obj, prefix = '', out = {}) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key, out); else out[key] = v;
  }
  return out;
};

const STRINGS = { fa: flatten(fa), en: flatten(en), ar: flatten(ar) };

const owns = (key) => Object.prototype.hasOwnProperty.call(STRINGS.fa, key);

/**
 * Look a key up in the local table (Persian as the fallback). The brand's
 * overlay and tokens apply as in the kit's catalogues (src/lib/brandText.js).
 */
export function localOnboardingString(locale, key) {
  const { value, from } = lookupLocal(STRINGS, locale, key);
  return value == null ? key : fillTemplate(value, undefined, from);
}

/** `t` for this screen: the kit catalogue when it has the key, the local table otherwise. */
export function useOnboardingStrings() {
  const { t, has, locale } = useTranslation();
  return useCallback(
    (key, vars) => (has(key) || !owns(key) ? t(key, vars) : localOnboardingString(locale, key)),
    [t, has, locale],
  );
}

export const ONBOARDING_STRING_LOCALES = Object.keys(STRINGS);
export const ONBOARDING_STRING_KEYS = Object.keys(STRINGS.fa);
export const onboardingStringTable = (locale) => STRINGS[locale];
